import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { useIntro } from "@/context/IntroContext";
import { mediaUrl, openWhatsApp, track } from "@/lib/api";
import { Reveal, SectionHead, EASE, Stars } from "@/components/Reveal";
import { Icon } from "@/lib/icons";
import Timeline from "@/components/Timeline";

function HeroMedia({ hero }) {
  const overlay = typeof hero.overlay === "number" ? hero.overlay : 0.6;
  if (hero.mode === "youtube" && hero.youtube_url) {
    const m = hero.youtube_url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{6,})/);
    const id = m ? m[1] : "";
    return (
      <div className="absolute inset-0 overflow-hidden">
        <iframe
          title="hero video"
          className="absolute inset-0 h-full w-full"
          src={id ? `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&controls=0&playsinline=1&playlist=${id}` : ""}
          allow="autoplay; encrypted-media"
        />
        <div className="absolute inset-0 bg-[#0B0B14]" style={{ opacity: overlay }} />
      </div>
    );
  }
  if (hero.mode === "video" && hero.video_url) {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <video src={mediaUrl(hero.video_url)} autoPlay muted loop playsInline className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0B0B14]" style={{ opacity: overlay }} />
      </div>
    );
  }
  return (
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url('${mediaUrl(hero.image_url) || "/logo.svg"}')` }}
    />
  );
}

function renderHighlight(line, word) {
  if (!word || !line.includes(word)) return line;
  const parts = line.split(word);
  return parts.map((p, i) => (
    <span key={i}>
      {p}
      {i < parts.length - 1 && <span className="text-grad">{word}</span>}
    </span>
  ));
}

function Hero() {
  const { settings } = useSite();
  const { done } = useIntro();
  const hero = settings?.hero || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const frameY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  const lines = [hero.title_line_1, hero.title_line_2].filter(Boolean);
  const overlay = typeof hero.overlay === "number" ? hero.overlay : 0.6;

  const ctaHref = (cta) => {
    if (!cta) return "#";
    if (String(cta.href).startsWith("whatsapp:")) {
      const tpl = settings?.templates?.general || "Hi!";
      return `https://wa.me/${String(settings?.contact?.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(tpl)}`;
    }
    return cta.href;
  };

  return (
    <section ref={ref} className="relative lg:min-h-screen flex items-start lg:items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <HeroMedia hero={hero} />
        {hero.mode === "image" && <div className="absolute inset-0 bg-[#0B0B14]" style={{ opacity: overlay }} />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B14] via-[#0B0B14]/75 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B0B14] to-transparent" />
      </div>

      <motion.div style={{ y: glowY }} className="ambient-glow w-[600px] h-[400px] -top-40 -right-40 opacity-60" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-10 lg:py-32 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center w-full">
        <div className="lg:col-span-7">
          {hero.badge && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={done ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-8"
              data-testid="hero-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-accent-bright node-pulse" />
              <span className="font-mono-label text-[10px] text-mist">{hero.badge}</span>
            </motion.div>
          )}

          <h1 className="font-display font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={done ? { y: 0 } : { y: "112%" }}
                  transition={{ duration: 0.95, delay: 0.2 + i * 0.14, ease: EASE }}
                  data-testid={`hero-title-line-${i + 1}`}
                >
                  {i === lines.length - 1 ? renderHighlight(line, hero.highlight_word) : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={done ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl text-sm sm:text-base lg:text-lg text-mist leading-relaxed"
            data-testid="hero-subtitle"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={done ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-10 hidden lg:flex flex-wrap items-center gap-4"
            data-testid="hero-cta-row"
          >
            {hero.cta_primary?.label && (
              <a
                href={ctaHref(hero.cta_primary)}
                target={String(hero.cta_primary.href).startsWith("whatsapp:") ? "_blank" : undefined}
                rel="noopener noreferrer"
                onClick={() => track("button_click", "/hero-primary")}
                className="group inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent-bright text-white font-semibold px-7 py-3.5 text-sm shadow-[0_0_30px_rgba(124,58,237,0.5)] hover:shadow-[0_0_44px_rgba(168,85,247,0.65)] transition-all duration-300 hover:-translate-y-0.5"
                data-testid="hero-cta-primary"
              >
                {hero.cta_primary.label}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            )}
            {hero.cta_secondary?.label && (
              <a
                href={ctaHref(hero.cta_secondary)}
                onClick={() => track("button_click", "/hero-secondary")}
                className="inline-flex items-center gap-2 rounded-full glass px-7 py-3.5 text-sm font-semibold text-white hover:border-accent/50 transition-all duration-300 hover:-translate-y-0.5"
                data-testid="hero-cta-secondary"
              >
                {hero.cta_secondary.label}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </motion.div>
        </div>

        <div className="hidden lg:block lg:col-span-5">
          <motion.div style={{ y: frameY }} initial={{ opacity: 0, scale: 0.94 }} animate={done ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.94 }} transition={{ duration: 1.1, delay: 0.5, ease: EASE }}>
            <div className="spot-frame aspect-[4/5] max-h-[560px] w-full" data-testid="hero-visual-frame">
              <img src={mediaUrl(hero.image_url)} alt="Studio work showcase" loading="eager" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={done ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
              transition={{ duration: 0.8, delay: 1, ease: EASE }}
              className="glass rounded-2xl px-5 py-4 -mt-14 ml-[-2rem] relative w-fit float-y"
              data-testid="hero-floating-chip"
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-accent/20 flex items-center justify-center">
                  <Check className="h-4 w-4 text-accent-bright" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-white">Launch in days</p>
                  <p className="text-[11px] text-mist">Design • Ads • Content — one studio</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={done ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-subtle"
      >
        <span className="font-mono-label text-[9px]">Scroll</span>
        <div className="h-10 w-px bg-gradient-to-b from-accent-bright to-transparent" />
      </motion.div>
    </section>
  );
}

function Marquee() {
  const items = ["Website Development", "Meta Ads", "Lead Generation", "UGC Videos", "Google Ads", "Landing Pages", "WhatsApp Funnels", "SEO Foundations", "Brand Systems"];
  const row = items.map((t, i) => (
    <span key={i} className="flex items-center gap-10 shrink-0">
      <span className="font-display text-sm sm:text-base font-semibold tracking-wide text-white/70 uppercase">{t}</span>
      <span className="h-1.5 w-1.5 rounded-full bg-accent-bright/70" />
    </span>
  ));
  return (
    <div className="relative border-y border-white/10 bg-[#131327]/60 py-5 overflow-hidden marquee-mask" data-testid="editorial-marquee">
      <div className="animate-marquee flex gap-10 w-max">
        <div className="flex gap-10">{row}</div>
        <div className="flex gap-10" aria-hidden="true">{row}</div>
      </div>
    </div>
  );
}

function Manifesto() {
  const { settings } = useSite();
  const ws = settings?.why_scalovix || {};
  const chapters = ws.chapters || [];
  return (
    <section className="relative py-16 sm:py-24 lg:py-32" data-testid="why-scalovix-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <SectionHead eyebrow="The Manifesto" title={ws.heading || "Why Scalovix"} sub={ws.sub} align="left" />
              <div className="hidden lg:block spot-frame aspect-[4/3] mt-2">
                <img src={mediaUrl(chapters[0]?.image_url)} alt={chapters[0]?.title || "Studio"} loading="lazy" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-16 sm:space-y-20">
            {chapters.map((c, i) => (
              <Reveal key={i} className="relative">
                <span className="pointer-events-none select-none absolute -top-10 right-0 font-display font-extrabold text-[6rem] sm:text-[8rem] leading-none text-white/[0.045]" aria-hidden="true">
                  {String(c.num || i + 1).padStart(2, "0")}
                </span>
                <p className="font-mono-label text-[11px] text-accent-bright mb-3">Chapter {String(c.num || i + 1).padStart(2, "0")}</p>
                <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-white mb-4 max-w-lg">{c.title}</h3>
                <p className="text-sm sm:text-base text-mist leading-relaxed max-w-xl">{c.body}</p>
                {c.image_url && (
                  <div className="lg:hidden mt-6 spot-frame aspect-[16/9]">
                    <img src={mediaUrl(c.image_url)} alt={c.title} loading="lazy" />
                  </div>
                )}
                <div className="mt-8 h-px bg-gradient-to-r from-accent/40 via-white/10 to-transparent" />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesBento() {
  const { settings, services } = useSite();
  const spans = ["md:col-span-8 md:row-span-2", "md:col-span-4", "md:col-span-4", "md:col-span-12"];
  return (
    <section id="services" className="relative py-16 sm:py-24 lg:py-32 bg-[#131327]/40 border-y border-white/[0.06]" data-testid="services-bento-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="What We Do"
          title="Services that move the needle"
          sub="Four focused offers, one outcome: measurable growth for your business. Transparent ₹ pricing, always."
        />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 sm:gap-6 md:auto-rows-[minmax(210px,auto)]">
          {services.map((s, i) => (
            <Reveal
              key={s.id || i}
              delay={i * 0.06}
              className={`group relative glass rounded-3xl overflow-hidden transition-all duration-500 hover:border-accent/40 hover:shadow-[0_0_50px_rgba(124,58,237,0.25)] hover:-translate-y-1 ${spans[i % spans.length]}`}
            >
              <Link to={`/services/${s.slug}`} className="flex flex-col h-full" data-testid={`service-card-${s.slug}`}>
                {i === 0 && s.image_url && (
                  <div className="relative h-44 sm:h-52 lg:h-56 overflow-hidden">
                    <img src={mediaUrl(s.image_url)} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B34] to-transparent" />
                  </div>
                )}
                {i === 3 && s.image_url && (
                  <div className="relative h-40 overflow-hidden md:absolute md:right-0 md:top-0 md:h-full md:w-2/5">
                    <img src={mediaUrl(s.image_url)} alt={s.title} loading="lazy" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#1B1B34] via-[#1B1B34]/40 to-transparent md:bg-gradient-to-l" />
                  </div>
                )}
                <div className="relative flex-1 flex flex-col p-6 sm:p-8">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <span className="h-11 w-11 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center">
                      <Icon name={s.icon || "Sparkles"} className="h-5 w-5 text-accent-bright" />
                    </span>
                    {s.badge && (
                      <span className="font-mono-label text-[9px] text-accent-bright border border-accent/30 bg-accent/10 rounded-full px-3 py-1.5" data-testid={`service-badge-${s.slug}`}>
                        {s.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-lg sm:text-xl lg:text-2xl font-bold text-white tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-sm text-mist leading-relaxed flex-1">{s.tagline}</p>
                  <div className="mt-5 flex items-end justify-between gap-3 flex-wrap">
                    <div className="flex items-baseline gap-2.5">
                      <span className="font-display text-xl sm:text-2xl font-bold text-white" data-testid={`service-price-${s.slug}`}>{s.price}</span>
                      {s.strike_price && <span className="text-sm text-subtle line-through">{s.strike_price}</span>}
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-bright group-hover:gap-2.5 transition-all duration-300">
                      View Details <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  const { settings } = useSite();
  const w = settings?.why_choose_us || {};
  return (
    <section className="py-16 sm:py-24 lg:py-32" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="The Difference" title={w.heading || "Why Choose Us"} sub={w.sub} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {(w.items || []).map((item, i) => (
            <Reveal key={i} delay={(i % 3) * 0.07}>
              <div className="group h-full glass rounded-2xl p-6 sm:p-7 transition-all duration-500 hover:border-accent/40 hover:shadow-[0_0_40px_rgba(124,58,237,0.2)] hover:-translate-y-1" data-testid={`feature-card-${i}`}>
                <span className="inline-flex h-12 w-12 rounded-xl bg-accent/15 border border-accent/25 items-center justify-center mb-5 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                  <Icon name={item.icon || "Sparkles"} className="h-5 w-5 text-accent-bright" />
                </span>
                <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-mist leading-relaxed">{item.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  const { settings } = useSite();
  const p = settings?.process || {};
  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-[#131327]/40 border-y border-white/[0.06]" data-testid="work-process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="How We Work" title={p.heading || "Our Work Process"} sub={p.sub} />
        <Timeline steps={p.steps} />
      </div>
    </section>
  );
}

function Proofs() {
  const { proofs } = useSite();
  if (!proofs || proofs.length === 0) return null;
  return (
    <section className="py-16 sm:py-24 lg:py-32" data-testid="proofs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Receipts" title="Proof, not promises" sub="Real dashboards, real numbers — from campaigns and websites we run every day." />
        <div className="flex gap-5 sm:gap-6 overflow-x-auto pb-4 hide-scrollbar snap-x snap-mandatory">
          {proofs.map((p, i) => (
            <Reveal key={p.id || i} delay={i * 0.05} className="shrink-0 w-[300px] sm:w-[360px] snap-start">
              <div className="spot-frame aspect-[4/3]" data-testid={`proof-card-${i}`}>
                <img src={mediaUrl(p.image_url)} alt={p.title} loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 z-10 p-5 bg-gradient-to-t from-[#0B0B14]/95 to-transparent">
                  {p.metric && <span className="inline-block font-mono-label text-[9px] text-emerald-400 bg-emerald-400/10 border border-emerald-400/25 rounded-full px-3 py-1 mb-2">{p.metric}</span>}
                  <p className="text-sm font-semibold text-white leading-snug">{p.title}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { testimonials } = useSite();
  if (!testimonials || testimonials.length === 0) return null;
  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-[#131327]/40 border-y border-white/[0.06]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Client Love" title="What founders say about us" sub="Unfiltered words from businesses we've helped grow." />
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
          {testimonials.map((t, i) => (
            <Reveal key={t.id || i} delay={(i % 3) * 0.06} className="break-inside-avoid mb-6">
              <div className="glass rounded-2xl p-6 sm:p-7 transition-all duration-500 hover:border-accent/35" data-testid={`testimonial-card-${i}`}>
                <Stars rating={t.rating} className="mb-4" />
                <p className="text-sm text-mist leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  {t.avatar_url ? (
                    <img src={mediaUrl(t.avatar_url)} alt={t.name} loading="lazy" className="h-10 w-10 rounded-full object-cover border border-white/15" />
                  ) : (
                    <span className="h-10 w-10 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center text-xs font-bold text-accent-bright">
                      {(t.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="text-xs text-subtle">{[t.role, t.company].filter(Boolean).join(", ")}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-4">
          <Link to="/testimonials" className="inline-flex items-center gap-2 text-sm font-semibold text-accent-bright hover:gap-3.5 transition-all duration-300" data-testid="testimonials-view-all-link">
            View all testimonials <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function FaqSection() {
  const { settings, faqs } = useSite();
  const list = (faqs || []).slice(0, 6);
  if (list.length === 0) return null;
  return (
    <section className="py-16 sm:py-24 lg:py-32" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow="Questions" title="Frequently asked questions" sub="Everything you want to know before working with us." />
        <Accordion type="single" collapsible className="space-y-3">
          {list.map((f, i) => (
            <Reveal key={f.id || i} delay={i * 0.04}>
              <AccordionItem value={`faq-${i}`} className="glass rounded-2xl border-none px-6 mb-3">
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-white hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                  {f.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-mist leading-relaxed pb-5">{f.answer}</AccordionContent>
              </AccordionItem>
            </Reveal>
          ))}
        </Accordion>
        <Reveal className="text-center mt-6">
          <Link to="/faq" className="text-sm font-semibold text-accent-bright hover:underline" data-testid="faq-view-all-link">
            See all FAQs
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function CtaBanner() {
  const { settings } = useSite();
  const cta = settings?.cta_banner || {};
  const isWa = String(cta.button_href || "").startsWith("whatsapp:");
  const href = isWa
    ? `https://wa.me/${String(settings?.contact?.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(settings?.templates?.general || "Hi!")}`
    : cta.button_href || "/contact";
  return (
    <section className="relative py-20 sm:py-28 overflow-hidden" data-testid="cta-banner-section">
      <div className="ambient-glow w-[700px] h-[380px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-70" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-5">{cta.heading}</h2>
          <p className="text-sm sm:text-base text-mist max-w-xl mx-auto mb-10">{cta.sub}</p>
          <a
            href={href}
            target={isWa ? "_blank" : undefined}
            rel="noopener noreferrer"
            onClick={() => track("button_click", "/cta-banner")}
            className="inline-flex items-center gap-2.5 rounded-full bg-accent hover:bg-accent-bright text-white font-semibold px-9 py-4 text-sm sm:text-base shadow-[0_0_40px_rgba(124,58,237,0.55)] hover:shadow-[0_0_60px_rgba(168,85,247,0.7)] transition-all duration-300 hover:-translate-y-1"
            data-testid="cta-banner-button"
          >
            {cta.button_label}
            <ArrowRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <Manifesto />
      <ServicesBento />
      <WhyChooseUs />
      <Process />
      <Proofs />
      <Testimonials />
      <FaqSection />
      <CtaBanner />
    </>
  );
}
